import { useEffect } from "react";

interface WakeLockSentinel {
  released: boolean;
  release(): Promise<void>;
}

/** Keeps the screen awake while pages are still owed to the server.
 *
 * This is the difference between a backlog that drains and one that doesn't.
 * A phone that locks its screen backgrounds this tab, and a backgrounded tab
 * uploads nothing — the 5s sweep skips a hidden document, and the browser
 * suspends whatever was mid-flight. So a scanner who photographs thirty
 * scripts and pockets the phone comes back to the same thirty scripts
 * unsent, with no error anywhere, because nothing failed: nothing ran.
 *
 * The lock is only held while the app is in the foreground *and* there is
 * work left, so it costs battery exactly while it's buying uploads.
 * Unsupported browsers (iOS Safari, at the time of writing) get nothing
 * here and keep relying on the scanner leaving the app open. */
export function useUploadWakeLock(hasPendingWork: boolean): void {
  useEffect(() => {
    if (!hasPendingWork) return;
    const wakeLock = (navigator as Navigator & { wakeLock?: { request(type: "screen"): Promise<WakeLockSentinel> } })
      .wakeLock;
    if (!wakeLock) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    async function acquire() {
      if (cancelled || document.hidden || (sentinel && !sentinel.released)) return;
      try {
        sentinel = await wakeLock!.request("screen");
      } catch {
        // Denied (no user gesture yet, battery saver, headless). Uploads
        // still work whenever the app is open; this is an optimisation.
      }
    }

    // The browser releases the lock on its own whenever the page hides, so
    // it has to be taken again every time the scanner comes back.
    const onVisible = () => {
      if (!document.hidden) void acquire();
    };
    void acquire();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      void sentinel?.release().catch(() => {});
    };
  }, [hasPendingWork]);
}
